import React, { useEffect, useState, useCallback } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { Box, Typography, useMediaQuery, useTheme, CircularProgress } from '@mui/material';
import NotificationCard from '../../customComponents/NotificationCard';
import ResponsiveView from '../../customComponents/ResponsiveView';
import CenteredLayout from '../../layout/CenteredLayout';
import PageContainerLayout from '../../layout/PageContainerLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import NoDataContainer from '../../components/NoDataContainer';
import { useNotification } from '../../context/NotificationContext';
import { getPushHistory } from '../../lib/api/user/userApi';
import { PushHistoryData } from '../../lib/api/user/userApi.types';
import { useLang } from '../../context/LangContext';

// 다국어 텍스트 객체
const notificationTexts = {
  ko: {
    title: '알림',
    loading: '알림을 불러오는 중...',
    loadFailed: '알림을 불러오는데 실패했습니다',
    noNotifications: '도착한 알림이 없습니다'
  },
  en: {
    title: 'Notifications',
    loading: 'Loading notifications...',
    loadFailed: 'Failed to load notifications',
    noNotifications: 'No notifications received'
  }
};

const NotificationPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // 768px 미만을 모바일로 판단
  const { markNotificationAsRead } = useNotification();
  const { lang } = useLang();
  const texts = notificationTexts[lang];
  
  // 상태 관리
  const [notifications, setNotifications] = useState<PushHistoryData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // 푸쉬 내역 조회 함수
  const fetchPushHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPushHistory({ page: 1, limit: 30 });
      
      if (response.data.statusCode === 200) {
        setNotifications(response.data.data);
      } else {
        setError(texts.loadFailed);
      }
    } catch (err) {
      devError('푸쉬 내역 조회 실패:', err);
      setError(texts.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [texts.loadFailed]);

  // 페이지 진입 시 알림을 읽음으로 표시하고 데이터 로드
  useEffect(() => {
    markNotificationAsRead();
    fetchPushHistory();
    devLog('알림 페이지 진입: 모든 알림을 읽음으로 표시');
  }, [markNotificationAsRead, fetchPushHistory]);


  // 알림 목록 렌더링 함수
  const renderNotificationList = () => (
    <Box sx={{ 
      backgroundColor: '#ffffff',
      minHeight: isMobile ? 'calc(100vh - 60px)' : 'calc(100vh - 150px)',
      paddingTop: isMobile ? '70px' : '0' // 모바일에서만 앱바 높이만큼 패딩 추가
    }}>
      {loading ? (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          textAlign: 'center'
        }}>
          <CircularProgress />
          <Typography sx={{
            color: '#999999',
            fontSize: '16px',
            fontWeight: 500,
            marginTop: '16px'
          }}>
            {texts.loading}
          </Typography>
        </Box>
      ) : error ? (
        <NoDataContainer message={texts.loadFailed} />
      ) : notifications.length === 0 ? (
        <NoDataContainer message={texts.noNotifications} />
      ) : (
        <Box>
          {notifications.map((notification) => (
            <NotificationCard
              key={notification.index}
              title={notification.title}
              content={notification.body}
              createdTime={notification.createAt}
              to={notification.to}
            />
          ))}
        </Box>
      )}
    </Box>
  );

  // 모바일 렌더링
  if (isMobile) {
    return (
      <ResponsiveView>
        <ProjectAppbar 
          title={texts.title} 
          showBackButton={true}
          showMobileSearch={false}
        />
        {renderNotificationList()}
      </ResponsiveView>
    );
  }

  // 웹 렌더링 (레이아웃 시스템 적용)
  return (
    <CenteredLayout>
      <ProjectAppbar 
        title={texts.title} 
        showBackButton={true}
        showMobileSearch={false}
      />
      <PageContainerLayout>
        {renderNotificationList()}
      </PageContainerLayout>
    </CenteredLayout>
  );
};

export default NotificationPage;
